import {
  Accordion,
  Box,
  Flex,
  Link,
  StarCircledIcon,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';

import { appliedRedemptionLabel } from '@jane/monolith/utils/cart';
import type { CrmIntegration } from '@jane/shared/types';

import { ApplyableDiscount } from './applyableDiscount';

/** The redemption fields the drawer reads; v1 and v3 cart redemptions both fit. */
export interface AppliedRedemption {
  readonly crm_reward_id: string | null;
  readonly id?: string;
  readonly point_value: number;
  readonly reward_name: string | null;
}

// `pluralize` caps counts at "1,000+", which is wrong for a points balance.
export const formatPoints = (points: number) =>
  `${points.toLocaleString('en-US')} ${pluralize({
    noun: 'pt',
    number: points,
    returnValue: 'word',
  })}`;

export const CustomerLoyaltyPointsAccordionHeader = ({
  appliedRedemptions,
  availablePoints,
  crmIntegration: { wallet_state, jane_redeemable_rewards },
  handleRemoveCrmRedemption,
  loading,
  storeRewardLabel,
}: {
  appliedRedemptions: readonly AppliedRedemption[];
  availablePoints: number;
  crmIntegration: CrmIntegration;
  handleRemoveCrmRedemption: (id: string) => void;
  loading: boolean;
  storeRewardLabel?: string | null;
}) => {
  const headingTitle = `Use store ${
    storeRewardLabel ? storeRewardLabel : 'rewards'
  }`;
  const isMobile = useMobileMediaQuery({});

  if (loading) {
    return <Header>{headingTitle}</Header>;
  }

  return (
    <Accordion.Heading px={isMobile ? 24 : 40} py={20}>
      <Header
        {...(wallet_state === 'unlocked' && {
          points: formatPoints(availablePoints),
        })}
      >
        {headingTitle}
      </Header>
      <Box pl={40}>
        {wallet_state === 'locked' && (
          <Typography>Authenticate your account to unlock rewards</Typography>
        )}
        {appliedRedemptions.length > 0 &&
          jane_redeemable_rewards.length > 0 && (
            <Box pt={16}>
              <Typography variant="body-semibold">Applied rewards</Typography>
              <ul>
                {appliedRedemptions.map((redemption) => (
                  <ApplyableDiscount
                    key={redemption.id}
                    applied
                    dataTestId="applied-redemption"
                    enabled
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCrmRedemption(
                        redemption.id?.toString() || ''
                      );
                    }}
                    text={`${appliedRedemptionLabel(redemption)} `}
                  >
                    <Link>Remove</Link>
                  </ApplyableDiscount>
                ))}
              </ul>
            </Box>
          )}
      </Box>
    </Accordion.Heading>
  );
};

const Header = ({
  children,
  points,
}: PropsWithChildren<{ points?: string }>) => (
  <Flex alignItems="center" justifyContent="space-between">
    <Flex alignItems="center">
      <StarCircledIcon />
      <Typography color="grays-black" ml={16} variant="body-semibold">
        {children}
      </Typography>
    </Flex>
    <Flex alignItems="center" gap={16}>
      {points && (
        <Typography color="grays-black" variant="body-semibold">
          {points}
        </Typography>
      )}
      <Accordion.Indicator iconSize="md" />
    </Flex>
  </Flex>
);
